import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useContribuyentesSearch } from "../hooks/useContribuyentesSearch";
import { ContribuyenteCombobox } from "../components/ContribuyenteCombobox";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty, CommandGroup } from "../components/ui/command";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Wallet, CheckCircle, XCircle, Download, Eye, Upload, FileText, Search, MessageCircle, Ban, Mail, FileSearch, ChevronsUpDown, Check, FileSpreadsheet, FileJson, Clock, Pencil } from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { loadPalmaLogo, drawPalmaFooter, fechaHoraVE } from "../lib/pdfFooter";

const ESTATUS_OPTS = [
  { value: "pendiente_validacion", label: "Pendiente" },
  { value: "validado", label: "Validado" },
  { value: "rechazado", label: "Rechazado" },
];
const estatusLabel = (v) => ESTATUS_OPTS.find(o => o.value === v)?.label || v || "-";
const estatusBadge = (v) => v === "validado"
  ? <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Validado</Badge>
  : v === "rechazado"
  ? <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100">Rechazado</Badge>
  : v === "anulado"
  ? <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200 line-through">Anulado</Badge>
  : <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">Pendiente</Badge>;

// Badge grande + pill de estado usado dentro del diálogo "Ver detalle".
// Colores verde/rojo/gris/naranja según el estado de validación del pago.
const ESTATUS_STYLE = {
  validado:              { cls: "bg-green-100 text-green-800 border-green-200",   label: "Validado",   Icon: CheckCircle },
  rechazado:             { cls: "bg-rose-100 text-rose-800 border-rose-200",      label: "Rechazado",  Icon: XCircle },
  anulado:               { cls: "bg-slate-200 text-slate-700 border-slate-300",   label: "Anulado",    Icon: Ban },
  pendiente_validacion:  { cls: "bg-orange-100 text-orange-800 border-orange-200", label: "Pendiente de validación", Icon: Clock },
};
const EstadoPagoPill = ({ estado }) => {
  const meta = ESTATUS_STYLE[estado] || ESTATUS_STYLE.pendiente_validacion;
  const { Icon } = meta;
  return (
    <span
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-semibold ${meta.cls}`}
      data-testid={`detail-estatus-pill-${estado || "pendiente"}`}
    >
      <Icon className="w-4 h-4" />
      {meta.label}
    </span>
  );
};

const tipoCuentaLabel = (t) => ({
  transferencia: "Transferencia",
  pago_movil: "Pago Móvil",
  punto_venta: "Punto de Venta",
  deposito: "Depósito",
  corriente: "Corriente",
  ahorro: "Ahorro",
}[t] || t || "—");

export default function RecaudacionesPage() {
  const { token, hasPermission } = useAuth();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [tab, setTab] = useState("registrar");
  const [pagos, setPagos] = useState([]);
  const [detail, setDetail] = useState(null);
  const [motivoRechazo, setMotivoRechazo] = useState("");
  // Anular recibo: diálogo con radio "Sin pagar" / "Anulada"
  const [anularDialog, setAnularDialog] = useState({ open: false, pago: null, planilla_estado: "sin_pagar", motivo: "", busy: false });
  // Editar pago pendiente: diálogo con campos editables + reemplazo de comprobante
  const [editDialog, setEditDialog] = useState({ open: false, pago: null, form: null, busy: false, file: null });
  const [editBancos, setEditBancos] = useState([]);
  const [editCuentas, setEditCuentas] = useState([]);

  // Filtros por pestaña (independientes)
  const emptyFilters = { rif: "", contribuyente: "", desde: "", hasta: "", estado: "todos", metodo: "todos" };
  const [filtVal, setFiltVal] = useState(emptyFilters);
  const [filtHist, setFiltHist] = useState(emptyFilters);

  // Aplica filtros AND sobre la lista ya cargada
  const applyFilters = (list, f, includeEstado) => {
    const rif = f.rif.trim().toLowerCase();
    const cn = f.contribuyente.trim().toLowerCase();
    return list.filter(p => {
      if (rif && !(p.contribuyente_rif || "").toLowerCase().includes(rif)) return false;
      if (cn && !(p.contribuyente_nombre || "").toLowerCase().includes(cn)) return false;
      if (f.desde && (p.fecha_pago || "") < f.desde) return false;
      if (f.hasta && (p.fecha_pago || "") > f.hasta) return false;
      if (f.metodo !== "todos" && (p.metodo_pago || "") !== f.metodo) return false;
      if (includeEstado && f.estado !== "todos" && (p.estado_validacion || "") !== f.estado) return false;
      return true;
    });
  };
  const pagosValidar = useMemo(() => applyFilters(pagos, filtVal, false), [pagos, filtVal]);
  const pagosHistorial = useMemo(() => applyFilters(pagos, filtHist, true), [pagos, filtHist]);

  const loadPagos = (estado) => axios.get(`${API}/recaudaciones`, { ...auth, params: estado ? { estado_validacion: estado } : {} }).then(r => setPagos(r.data));
  useEffect(() => {
    if (tab === "validar") loadPagos("pendiente_validacion");
    else if (tab === "historial") loadPagos();
  // eslint-disable-next-line
  }, [tab]);

  const validar = async (id) => {
    try {
      const r = await axios.post(`${API}/recaudaciones/pago/${id}/validar`, {}, auth);
      const nf = r.data?.notificacion || {};
      const emailMsg = {
        enviado: " · Email enviado ✉️",
        sin_email: " · Sin email del contribuyente",
        sin_api_key: " · Email pendiente (Resend no configurado)",
        error: " · Email falló al enviar",
      }[nf.email_status] || "";
      toast.success(`Pago validado${emailMsg}`);
      loadPagos("pendiente_validacion");
      window.dispatchEvent(new CustomEvent("sisaur:data-changed", { detail: { source: "validar-pago", pago_id: id } }));
    } catch (e) { toast.error(e.response?.data?.detail || "Error al validar"); }
  };
  const reenviarEmail = async (pago) => {
    try {
      const r = await axios.post(`${API}/recaudaciones/pago/${pago.id}/notificar`, {}, auth);
      const status = r.data?.email_status;
      if (status === "enviado") toast.success("Email reenviado al contribuyente ✉️");
      else if (status === "sin_email") toast.error("El contribuyente no tiene email registrado");
      else if (status === "sin_api_key") toast("Configure Resend API key en el .env para enviar emails", { icon: "⚠️" });
      else if (status === "error") toast.error("No se pudo enviar el email: " + (r.data?.email_error || ""));
      else toast(`Estado: ${status}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al reenviar"); }
  };
  const anular = (p) => {
    setAnularDialog({ open: true, pago: p, planilla_estado: "sin_pagar", motivo: "", busy: false });
  };
  const confirmarAnular = async () => {
    const { pago, planilla_estado, motivo } = anularDialog;
    if (!pago) return;
    if (planilla_estado === "anulada" && !motivo.trim()) {
      toast.error("Debe indicar un motivo cuando la planilla queda 'Anulada'");
      return;
    }
    setAnularDialog(d => ({ ...d, busy: true }));
    try {
      const r = await axios.post(`${API}/recaudaciones/pago/${pago.id}/anular`, {
        motivo: motivo.trim(), planilla_estado,
      }, auth);
      const planillaLabel = planilla_estado === "anulada" ? "Anuladas" : "Sin pagar";
      toast.success(`Recibo ${pago.nro_comprobante} anulado · ${r.data?.planillas_afectadas || 0} planilla(s) → ${planillaLabel}`);
      setAnularDialog({ open: false, pago: null, planilla_estado: "sin_pagar", motivo: "", busy: false });
      loadPagos();
      window.dispatchEvent(new CustomEvent("sisaur:data-changed", { detail: { source: "anular-pago", pago_id: pago.id, planilla_estado } }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al anular");
      setAnularDialog(d => ({ ...d, busy: false }));
    }
  };
  const rechazar = async (id) => {
    await axios.post(`${API}/recaudaciones/pago/${id}/rechazar`, { motivo: motivoRechazo }, auth);
    toast.success("Rechazado"); setMotivoRechazo(""); setDetail(null); loadPagos("pendiente_validacion");
    window.dispatchEvent(new CustomEvent("sisaur:data-changed", { detail: { source: "rechazar-pago", pago_id: id } }));
  };
  const verDetalle = async (id) => { const r = await axios.get(`${API}/recaudaciones/${id}`, auth); setDetail(r.data); };

  // ============ EDITAR pago pendiente ============
  const openEditar = async (pago) => {
    try {
      // Cargar catálogos si aún no están (una vez por sesión)
      if (editBancos.length === 0) {
        try { const rb = await axios.get(`${API}/definiciones/bancos`, auth); setEditBancos(rb.data || []); } catch { /* noop */ }
      }
      if (editCuentas.length === 0) {
        try { const rc = await axios.get(`${API}/definiciones/cuentas-bancarias`, auth); setEditCuentas(rc.data || []); } catch { /* noop */ }
      }
      // Pre-cargar el pago con la data actual
      const r = await axios.get(`${API}/recaudaciones/${pago.id}`, auth);
      const p = r.data || pago;
      setEditDialog({
        open: true,
        pago: p,
        form: {
          referencia:              p.referencia || "",
          fecha_pago:              p.fecha_pago || "",
          monto_pagado:            String(p.monto_pagado ?? ""),
          metodo_pago:             p.metodo_pago || "transferencia",
          banco_id:                p.banco_id || "",
          cuenta_recaudadora_id:   p.cuenta_recaudadora_id || "",
          observaciones:           p.observaciones || "",
          comprobante_url:         p.comprobante_url || "",
        },
        busy: false,
        file: null,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo abrir el pago");
    }
  };
  const setEditField = (k, v) => setEditDialog(d => ({ ...d, form: { ...d.form, [k]: v } }));
  const submitEditar = async () => {
    const { pago, form, file } = editDialog;
    if (!form) return;
    // Validaciones ligeras client-side
    const monto = parseFloat(form.monto_pagado);
    if (!(monto > 0)) { toast.error("El monto debe ser mayor a cero"); return; }
    if (!form.fecha_pago) { toast.error("Ingrese la fecha del pago"); return; }
    if (!form.referencia.trim()) { toast.error("Ingrese la referencia"); return; }

    setEditDialog(d => ({ ...d, busy: true }));
    try {
      let comprobante_url = form.comprobante_url;
      // Si el funcionario adjuntó un nuevo archivo, subirlo primero
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await axios.post(`${API}/recaudaciones/comprobante/upload`, fd, {
          ...auth, headers: { ...auth.headers, "Content-Type": "multipart/form-data" },
        });
        comprobante_url = up.data?.url || comprobante_url;
      }
      const payload = {
        referencia:            form.referencia.trim(),
        fecha_pago:            form.fecha_pago,
        monto_pagado:          monto,
        metodo_pago:           form.metodo_pago,
        banco_id:              form.banco_id || null,
        cuenta_recaudadora_id: form.cuenta_recaudadora_id || null,
        observaciones:         (form.observaciones || "").trim(),
        comprobante_url,
      };
      await axios.put(`${API}/recaudaciones/pago/${pago.id}/editar`, payload, auth);
      toast.success("Pago editado correctamente");
      setEditDialog({ open: false, pago: null, form: null, busy: false, file: null });
      loadPagos("pendiente_validacion");
      window.dispatchEvent(new CustomEvent("sisaur:data-changed", { detail: { source: "editar-pago", pago_id: pago.id } }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al editar el pago");
      setEditDialog(d => ({ ...d, busy: false }));
    }
  };
  const descargarPDF = async (id, nro) => {
    const r = await axios.get(`${API}/recaudaciones/${id}/recibo-pdf`, { ...auth, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a"); a.href = url; a.download = `recibo_${nro}.pdf`; a.click(); URL.revokeObjectURL(url);
  };

  // Abre el recibo en una pestaña nueva del navegador (visualización inline, sin descargar)
  const verPDF = async (id) => {
    try {
      const r = await axios.get(`${API}/recaudaciones/${id}/recibo-pdf`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) toast.error("El navegador bloqueó la ventana emergente. Permita popups para ver el recibo.");
      // Libera el objectURL a los 60s (tiempo prudente para que el visor cargue)
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo abrir el recibo");
    }
  };

  // Envía por WhatsApp el link público del recibo de pago con mensaje motivacional
  const enviarPagoWhatsApp = async (pago) => {
    try {
      // Obtener detalle completo si aún no incluye contribuyente
      let full = pago;
      if (!pago.contribuyente) {
        const r = await axios.get(`${API}/recaudaciones/${pago.id}`, auth);
        full = { ...pago, ...r.data };
      }
      const c = full.contribuyente || {};
      const raw = (c.celular || c.telefono || "").replace(/\D/g, "");
      if (!raw) { toast.error("El contribuyente no tiene celular registrado"); return; }
      const phone = raw.startsWith("58") ? raw : (raw.startsWith("0") ? "58" + raw.slice(1) : "58" + raw);
      // Obtener share_token para URL pública del recibo
      const st = await axios.get(`${API}/recaudaciones/${pago.id}/share-token`, auth);
      const shareToken = st.data?.share_token;
      const pdfUrl = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/recaudaciones/${pago.id}/recibo-pdf?token=${shareToken}`;
      const nombre = c.razon_social || `${c.nombre || ""} ${c.apellido || ""}`.trim() || "Contribuyente";
      const monto = `Bs. ${(Number(pago.monto_pagado)||0).toLocaleString("es-VE",{maximumFractionDigits:2})}`;
      const msg = `¡Hola ${nombre}! 🌿\n\n` +
                  `Palma Plus, Saneamiento Urbano le agradece por su compromiso ciudadano. Su pago de ${monto} correspondiente al recibo ${pago.nro_comprobante} ha sido validado exitosamente. 🎉\n\n` +
                  `📄 Puede descargar su comprobante oficial aquí:\n${pdfUrl}\n\n` +
                  `Con contribuyentes como usted construimos una comunidad más limpia, más ordenada y más saludable. Recuerde que cada aporte mantiene nuestras calles, avenidas y espacios públicos impecables.\n\n` +
                  `✨ Invitamos a toda su comunidad a cumplir puntualmente con el servicio de aseo urbano. ¡Cada pago cuenta!\n\n` +
                  `🌱 *Por una ciudad más limpia.*\n— Palma Plus, Saneamiento Urbano`;
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo enviar el recibo por WhatsApp");
    }
  };

  const fmt = (n) => `Bs. ${(Number(n)||0).toLocaleString("es-VE",{maximumFractionDigits:2})}`;

  return (
    <div className="space-y-6" data-testid="recaudaciones-page">
      <div>
        <h1 className="font-display font-extrabold text-3xl text-green-900 flex items-center gap-3"><Wallet className="w-8 h-8 text-orange-500" />Recaudaciones</h1>
        <p className="text-green-700/70 mt-1">Registro y validación de pagos</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-green-50 border border-green-100">
          <TabsTrigger value="registrar" data-testid="tab-registrar">Registrar recaudación</TabsTrigger>
          <TabsTrigger value="validar" data-testid="tab-validar">Pagos por validar</TabsTrigger>
          <TabsTrigger value="historial" data-testid="tab-historial">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="registrar" className="mt-4">
          <RegistrarRecaudacion auth={auth} onCreated={() => { if (tab === "historial") loadPagos(); }} />
        </TabsContent>

        <TabsContent value="validar" className="mt-4">
          <Card className="p-4 border-green-100">
            <h3 className="font-display font-bold text-lg text-green-900 mb-3">Pagos reportados por contribuyentes</h3>
            <FiltrosPagos filters={filtVal} onChange={setFiltVal} includeEstado={false} totalCount={pagos.length} filteredCount={pagosValidar.length} rowsToExport={pagosValidar} contexto="validar" />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-green-50 text-green-900"><tr><th className="text-left p-2.5">Nº Comprobante</th><th className="text-left p-2.5">RIF</th><th className="text-left p-2.5">Contribuyente</th><th className="text-left p-2.5">Fecha</th><th className="text-left p-2.5">Método</th><th className="text-left p-2.5">Referencia</th><th className="text-right p-2.5">Monto</th><th className="text-right p-2.5">Acciones</th></tr></thead>
                <tbody>{pagosValidar.map(p => (
                  <tr key={p.id} className="border-b border-green-50">
                    <td className="p-2.5 font-mono">{p.nro_comprobante || <span className="text-slate-400 italic">Sin asignar</span>}</td>
                    <td className="p-2.5 font-mono text-xs">{p.contribuyente_rif || "-"}</td>
                    <td className="p-2.5 max-w-[220px] truncate" title={p.contribuyente_nombre || ""}>{p.contribuyente_nombre || <span className="text-slate-400 italic">—</span>}</td>
                    <td className="p-2.5">{p.fecha_pago}</td>
                    <td className="p-2.5">{METODO_LABEL[p.metodo_pago] || p.metodo_pago}</td>
                    <td className="p-2.5 font-mono">{p.referencia||"-"}</td>
                    <td className="p-2.5 text-right font-semibold">{fmt(p.monto_pagado)}</td>
                    <td className="p-2.5 text-right space-x-1">
                      <Button size="icon" variant="ghost" onClick={() => verDetalle(p.id)} data-testid={`btn-ver-${p.nro_comprobante || p.id}`} title="Ver detalle"><Eye className="w-4 h-4 text-green-700" /></Button>
                      {hasPermission("recaudaciones.editar") && (
                        <Button size="icon" variant="ghost" onClick={() => openEditar(p)} data-testid={`btn-editar-${p.nro_comprobante || p.id}`} title="Editar pago"><Pencil className="w-4 h-4 text-blue-600" /></Button>
                      )}
                      <Button size="icon" variant="ghost" onClick={() => validar(p.id)} data-testid={`btn-validar-${p.nro_comprobante || p.id}`} title="Validar"><CheckCircle className="w-4 h-4 text-green-700" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDetail({...p, _rechazar:true})} title="Rechazar"><XCircle className="w-4 h-4 text-red-500" /></Button>
                    </td>
                  </tr>
                ))}{!pagos.length && <tr><td colSpan="8" className="p-8 text-center text-slate-400">Sin pagos por validar</td></tr>}</tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="historial" className="mt-4">
          <Card className="p-4 border-green-100">
            <h3 className="font-display font-bold text-lg text-green-900 mb-3">Historial de pagos</h3>
            <FiltrosPagos filters={filtHist} onChange={setFiltHist} includeEstado={true} totalCount={pagos.length} filteredCount={pagosHistorial.length} rowsToExport={pagosHistorial} contexto="historial" />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-green-50 text-green-900"><tr><th className="text-left p-2.5">Comprobante</th><th className="text-left p-2.5">RIF</th><th className="text-left p-2.5">Contribuyente</th><th className="text-left p-2.5">Fecha</th><th className="text-left p-2.5">Método</th><th className="text-right p-2.5">Monto</th><th className="text-center p-2.5">Estatus</th><th className="text-right p-2.5">Acciones</th></tr></thead>
                <tbody>{pagosHistorial.map(p => (
                  <tr key={p.id} className="border-b border-green-50">
                    <td className="p-2.5 font-mono">{p.nro_comprobante || <span className="text-slate-400 italic">Sin asignar</span>}</td>
                    <td className="p-2.5 font-mono text-xs">{p.contribuyente_rif || "-"}</td>
                    <td className="p-2.5 max-w-[220px] truncate" title={p.contribuyente_nombre || ""}>{p.contribuyente_nombre || <span className="text-slate-400 italic">—</span>}</td>
                    <td className="p-2.5">{p.fecha_pago}</td>
                    <td className="p-2.5">{METODO_LABEL[p.metodo_pago] || p.metodo_pago}</td>
                    <td className="p-2.5 text-right font-semibold">{fmt(p.monto_pagado)}</td>
                    <td className="p-2.5 text-center">{estatusBadge(p.estado_validacion)}</td>
                    <td className="p-2.5 text-right whitespace-nowrap">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => verDetalle(p.id)}
                        title="Ver detalle del pago"
                        data-testid={`hist-ver-detalle-${p.nro_comprobante || p.id}`}
                      >
                        <Eye className="w-4 h-4 text-green-700" />
                      </Button>
                      {p.estado_validacion === "validado" ? (
                        <>
                          <Button size="icon" variant="ghost" onClick={() => verPDF(p.id)} title="Ver recibo" data-testid={`hist-ver-${p.nro_comprobante}`}><FileSearch className="w-4 h-4 text-orange-600" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => descargarPDF(p.id, p.nro_comprobante)} title="Descargar recibo" data-testid={`hist-download-${p.nro_comprobante}`}><Download className="w-4 h-4 text-green-700" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => enviarPagoWhatsApp(p)} title="Enviar por WhatsApp" data-testid={`hist-wa-${p.nro_comprobante}`}><MessageCircle className="w-4 h-4 text-emerald-600" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => reenviarEmail(p)} title="Reenviar email al contribuyente" data-testid={`hist-email-${p.nro_comprobante}`}><Mail className="w-4 h-4 text-blue-600" /></Button>
                          {hasPermission("recaudaciones.anular") && <Button size="icon" variant="ghost" onClick={() => anular(p)} title="Anular recibo" data-testid={`hist-anular-${p.nro_comprobante}`}><Ban className="w-4 h-4 text-orange-600" /></Button>}
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}{!pagosHistorial.length && <tr><td colSpan="8" className="p-8 text-center text-slate-400">{pagos.length ? "Ningún pago coincide con los filtros" : "Sin pagos registrados"}</td></tr>}</tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!detail} onOpenChange={(o) => { if(!o){ setDetail(null); setMotivoRechazo(""); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{detail?._rechazar ? "Rechazar pago" : `Detalle del pago ${detail?.nro_comprobante||""}`}</DialogTitle></DialogHeader>
          {detail && !detail._rechazar && (
            <div className="space-y-3 text-sm">
              {/* Cabecera de estado — badge grande verde/rojo/gris/naranja */}
              <div className="flex items-center justify-between gap-3 pb-3 border-b border-slate-100" data-testid="detail-header">
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Estado del pago</span>
                  <EstadoPagoPill estado={detail.estado_validacion} />
                </div>
                <div className="text-right">
                  <span className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold block">Comprobante</span>
                  <span className="font-mono text-sm text-slate-800">{detail.nro_comprobante || <span className="italic text-slate-400">Sin asignar</span>}</span>
                </div>
              </div>
              {detail.motivo_rechazo && (
                <div className="rounded-lg bg-rose-50 border border-rose-200 p-2.5 text-[12.5px] text-rose-800" data-testid="detail-motivo-rechazo">
                  <strong>Motivo de rechazo:</strong> {detail.motivo_rechazo}
                </div>
              )}
              {detail.anulacion_motivo && (
                <div className="rounded-lg bg-slate-100 border border-slate-200 p-2.5 text-[12.5px] text-slate-700" data-testid="detail-motivo-anulacion">
                  <strong>Motivo de anulación:</strong> {detail.anulacion_motivo}
                </div>
              )}
              <p><strong>Contribuyente:</strong> {detail.contribuyente?.nombre} {detail.contribuyente?.apellido||""}</p>
              <p><strong>Documento:</strong> {detail.contribuyente?.tipo_documento}-{detail.contribuyente?.documento}</p>
              {(!detail.detalles_pago || detail.detalles_pago.length <= 1) && (
                <p><strong>Método:</strong> {detail.metodo_pago} · <strong>Referencia:</strong> {detail.referencia||"-"}</p>
              )}
              <p><strong>Monto total:</strong> {fmt(detail.monto_pagado)}</p>
              {detail.comprobante_url && (!detail.detalles_pago || detail.detalles_pago.length <= 1) && (
                <p><strong>Comprobante:</strong> <a className="text-orange-600 underline" href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${detail.comprobante_url}`} target="_blank" rel="noopener noreferrer">Ver archivo</a></p>
              )}
              {detail.detalles_pago && detail.detalles_pago.length > 1 && (
                <div className="border border-orange-200 bg-orange-50/50 rounded-lg p-3" data-testid="detail-multipago">
                  <p className="font-semibold text-orange-800 mb-2">
                    Pago multi-parte ({detail.detalles_pago.length} transferencias)
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="text-left text-slate-600">
                        <tr>
                          <th className="p-1">#</th>
                          <th className="p-1">Referencia</th>
                          <th className="p-1">Fecha</th>
                          <th className="p-1 text-right">Monto</th>
                          <th className="p-1">Comprobante</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.detalles_pago.map((sub, i) => (
                          <tr key={i} className="border-t border-orange-100">
                            <td className="p-1 font-mono">{i + 1}</td>
                            <td className="p-1 font-mono">{sub.referencia}</td>
                            <td className="p-1">{sub.fecha_pago}</td>
                            <td className="p-1 text-right font-semibold">{fmt(sub.monto_pagado)}</td>
                            <td className="p-1">
                              {sub.comprobante_url ? (
                                <a
                                  className="text-orange-600 underline"
                                  href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${sub.comprobante_url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >Ver</a>
                              ) : <span className="text-slate-400">—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <p><strong>Observaciones:</strong> {detail.observaciones||"-"}</p>
              <h4 className="font-semibold mt-3">Liquidaciones cubiertas:</h4>
              <ul className="ml-4 list-disc">{detail.liquidaciones?.map(l => <li key={l.id}>{l.periodo} · {l.descripcion} · {fmt(l.monto)}</li>)}</ul>
            </div>
          )}
          {detail?._rechazar && (
            <div className="space-y-3">
              <Label>Motivo del rechazo</Label>
              <Textarea data-testid="textarea-rechazo" value={motivoRechazo} onChange={e => setMotivoRechazo(e.target.value)} />
              <DialogFooter><Button data-testid="btn-confirm-rechazo" onClick={() => rechazar(detail.id)} className="bg-red-600 hover:bg-red-700 text-white">Confirmar rechazo</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Diálogo Editar pago pendiente */}
      <Dialog
        open={editDialog.open}
        onOpenChange={(o) => { if (!o) setEditDialog({ open: false, pago: null, form: null, busy: false, file: null }); }}
      >
        <DialogContent className="max-w-2xl" data-testid="dialog-editar-pago">
          <DialogHeader>
            <DialogTitle className="text-blue-800 flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Editar pago {editDialog.pago?.nro_comprobante || ""}
            </DialogTitle>
            <DialogDescription>
              Ajuste los datos del pago antes de validarlo. Los cambios quedan registrados en el
              historial de auditoría con su usuario y fecha.
            </DialogDescription>
          </DialogHeader>
          {editDialog.form && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <Label>Fecha del pago*</Label>
                <Input
                  type="date"
                  value={editDialog.form.fecha_pago}
                  onChange={(e) => setEditField("fecha_pago", e.target.value)}
                  data-testid="edit-pago-fecha"
                />
              </div>
              <div>
                <Label>Monto pagado (Bs)*</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={editDialog.form.monto_pagado}
                  onChange={(e) => setEditField("monto_pagado", e.target.value)}
                  data-testid="edit-pago-monto"
                />
              </div>
              <div>
                <Label>Método de pago*</Label>
                <select
                  className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm bg-white"
                  value={editDialog.form.metodo_pago}
                  onChange={(e) => setEditField("metodo_pago", e.target.value)}
                  data-testid="edit-pago-metodo"
                >
                  <option value="transferencia">Transferencia</option>
                  <option value="pago_movil">Pago Móvil</option>
                  <option value="efectivo">Efectivo</option>
                  <option value="punto_venta">Punto de Venta</option>
                  <option value="deposito">Depósito</option>
                </select>
              </div>
              <div>
                <Label>Referencia*</Label>
                <Input
                  value={editDialog.form.referencia}
                  onChange={(e) => setEditField("referencia", e.target.value)}
                  data-testid="edit-pago-referencia"
                  placeholder="Ej: 0055123456"
                />
              </div>
              <div>
                <Label>Banco de origen</Label>
                <select
                  className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm bg-white"
                  value={editDialog.form.banco_id || ""}
                  onChange={(e) => setEditField("banco_id", e.target.value)}
                  data-testid="edit-pago-banco"
                >
                  <option value="">— Seleccione —</option>
                  {editBancos.map((b) => (
                    <option key={b.id} value={b.id}>{b.codigo} · {b.nombre}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Cuenta recaudadora</Label>
                <select
                  className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm bg-white"
                  value={editDialog.form.cuenta_recaudadora_id || ""}
                  onChange={(e) => setEditField("cuenta_recaudadora_id", e.target.value)}
                  data-testid="edit-pago-cuenta"
                >
                  <option value="">— Seleccione —</option>
                  {editCuentas.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.banco_nombre || ""} · {c.numero_cuenta}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <Label>Observaciones</Label>
                <Textarea
                  rows={2}
                  value={editDialog.form.observaciones}
                  onChange={(e) => setEditField("observaciones", e.target.value)}
                  data-testid="edit-pago-observaciones"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Comprobante (JPG/PNG/PDF, máx. 8 MB)</Label>
                {editDialog.form.comprobante_url && !editDialog.file && (
                  <div className="text-xs text-slate-600 mb-1">
                    Actual:{" "}
                    <a
                      className="text-orange-600 underline"
                      href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${editDialog.form.comprobante_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >Ver archivo</a>
                  </div>
                )}
                <Input
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                  onChange={(e) => setEditDialog(d => ({ ...d, file: e.target.files?.[0] || null }))}
                  data-testid="edit-pago-comprobante"
                />
                {editDialog.file && (
                  <p className="text-[11px] text-green-700 mt-1">Nuevo: {editDialog.file.name}</p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDialog({ open: false, pago: null, form: null, busy: false, file: null })}
              disabled={editDialog.busy}
              data-testid="edit-pago-cancel"
            >Cancelar</Button>
            <Button
              onClick={submitEditar}
              disabled={editDialog.busy}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="edit-pago-submit"
            >
              <Pencil className="w-4 h-4 mr-1.5" />
              {editDialog.busy ? "Guardando…" : "Guardar cambios"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Diálogo Anular Recibo (Sin pagar / Anulada) */}
      <Dialog
        open={anularDialog.open}
        onOpenChange={(o) => { if (!o) setAnularDialog({ open: false, pago: null, planilla_estado: "sin_pagar", motivo: "", busy: false }); }}
      >
        <DialogContent className="max-w-lg" data-testid="dialog-anular-recibo">
          <DialogHeader>
            <DialogTitle className="text-orange-800 flex items-center gap-2">
              <Ban className="w-5 h-5" />
              Anular recibo {anularDialog.pago?.nro_comprobante || ""}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Elige en qué estatus queda la planilla de liquidación asociada al recibo anulado: Sin pagar o Anulada.
            </DialogDescription>
          </DialogHeader>
          {anularDialog.pago && (
            <div className="space-y-4">
              <p className="text-sm text-slate-700">
                Se conservará el correlativo <b className="font-mono">{anularDialog.pago.nro_comprobante}</b>, pero
                <b> ¿en qué estatus deseas que quede la planilla de liquidación?</b>
              </p>

              <RadioGroup
                value={anularDialog.planilla_estado}
                onValueChange={(v) => setAnularDialog(d => ({ ...d, planilla_estado: v }))}
                className="space-y-2"
                data-testid="radio-planilla-estado"
              >
                <label
                  htmlFor="planilla-sin-pagar"
                  className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition ${
                    anularDialog.planilla_estado === "sin_pagar"
                      ? "border-green-600 bg-green-50"
                      : "border-slate-200 hover:border-green-300 hover:bg-green-50/30"
                  }`}
                >
                  <RadioGroupItem value="sin_pagar" id="planilla-sin-pagar" className="mt-0.5" data-testid="radio-sin-pagar" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-green-900">Sin pagar</span>
                      <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100 text-[10px]">Recomendado</Badge>
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5">
                      La planilla vuelve a estar disponible para cobrar. Se sumará de nuevo a la deuda del contribuyente y podrá liquidarse otra vez.
                    </p>
                  </div>
                </label>

                <label
                  htmlFor="planilla-anulada"
                  className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition ${
                    anularDialog.planilla_estado === "anulada"
                      ? "border-red-600 bg-red-50"
                      : "border-slate-200 hover:border-red-300 hover:bg-red-50/30"
                  }`}
                >
                  <RadioGroupItem value="anulada" id="planilla-anulada" className="mt-0.5" data-testid="radio-anulada" />
                  <div className="flex-1">
                    <span className="font-semibold text-red-900">Anulada</span>
                    <p className="text-xs text-slate-600 mt-0.5">
                      La planilla queda <b>anulada de forma definitiva</b>: no se cobra, no cuenta como pagada, no genera intereses. Un funcionario podrá reactivarla luego si fue por error.
                    </p>
                  </div>
                </label>
              </RadioGroup>

              <div>
                <Label>
                  Motivo{" "}
                  {anularDialog.planilla_estado === "anulada"
                    ? <span className="text-red-500">*</span>
                    : <span className="text-slate-400 text-xs font-normal">(opcional)</span>}
                </Label>
                <Textarea
                  data-testid="input-motivo-anulacion"
                  value={anularDialog.motivo}
                  onChange={(e) => setAnularDialog(d => ({ ...d, motivo: e.target.value }))}
                  rows={3}
                  placeholder={
                    anularDialog.planilla_estado === "anulada"
                      ? "Ej.: Duplicidad de pago, error de tarifa, cierre del establecimiento…"
                      : "Ej.: Cheque devuelto, pago mal registrado…"
                  }
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Queda auditado con tu usuario y la fecha.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAnularDialog({ open: false, pago: null, planilla_estado: "sin_pagar", motivo: "", busy: false })}
              disabled={anularDialog.busy}
              data-testid="btn-cancelar-anular"
            >Cancelar</Button>
            <Button
              onClick={confirmarAnular}
              disabled={anularDialog.busy || (anularDialog.planilla_estado === "anulada" && !anularDialog.motivo.trim())}
              className={anularDialog.planilla_estado === "anulada"
                ? "bg-red-600 hover:bg-red-700 text-white"
                : "bg-orange-500 hover:bg-orange-600 text-white"}
              data-testid="btn-confirmar-anular"
            >
              {anularDialog.busy ? "Anulando…" : (
                anularDialog.planilla_estado === "anulada"
                  ? "Anular recibo y planillas"
                  : "Anular recibo (planillas Sin pagar)"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============================== Barra de filtros para tablas de pagos ==============================
const METODO_LABEL = { transferencia: "Transferencia", pago_movil: "Pago Móvil", efectivo: "Efectivo", punto_venta: "Punto de Venta", deposito: "Depósito" };
const ESTADO_LABEL = { validado: "Validado", pendiente_validacion: "Pendiente", rechazado: "Rechazado", anulado: "Anulado" };

// Aplana un pago a un objeto plano listo para exportar (columnas legibles)
function pagoToRow(p) {
  return {
    "Nº Comprobante": p.nro_comprobante || "",
    "RIF": p.contribuyente_rif || "",
    "Contribuyente": p.contribuyente_nombre || "",
    "Fecha Pago": p.fecha_pago || "",
    "Método": METODO_LABEL[p.metodo_pago] || p.metodo_pago || "",
    "Referencia": p.referencia || "",
    "Banco Emisor ID": p.banco_id || "",
    "Banco Receptor ID": p.banco_receptor_id || "",
    "Monto (Bs.)": Number(p.monto_pagado || 0),
    "Estatus": ESTADO_LABEL[p.estado_validacion] || p.estado_validacion || "",
    "Fecha Registro": (p.created_at || "").slice(0, 10),
    "Validado por": p.validado_por_nombre || "",
    "Motivo rechazo": p.motivo_rechazo || "",
  };
}

function exportarPagos({ formato, rows, contexto }) {
  if (!rows.length) { toast.error("Nada que exportar con los filtros actuales"); return; }
  const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  const filename = `recaudaciones_${contexto}_${stamp}`;

  if (formato === "excel") {
    const data = rows.map(pagoToRow);
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = Object.keys(data[0] || {}).map(k => ({ wch: Math.max(k.length + 2, 14) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Pagos");
    XLSX.writeFile(wb, `${filename}.xlsx`);
    toast.success(`Excel descargado (${rows.length} pagos)`);
  } else if (formato === "json") {
    const blob = new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${filename}.json`; a.click(); URL.revokeObjectURL(url);
    toast.success(`JSON descargado (${rows.length} pagos)`);
  } else if (formato === "pdf") {
    loadPalmaLogo().then(logoDataURL => {
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const { fecha, hora } = fechaHoraVE();
      doc.setFontSize(16); doc.setTextColor(20, 83, 45); doc.text("SISAUR · Recaudaciones", 40, 40);
      doc.setFontSize(11); doc.setTextColor(80);
      doc.text(`Reporte: ${contexto === "historial" ? "Historial de pagos" : "Pagos por validar"}`, 40, 58);
      doc.text(`Generado: ${fecha} ${hora} · Total: ${rows.length} pagos`, 40, 74);
      const total = rows.reduce((s, p) => s + Number(p.monto_pagado || 0), 0);
      doc.text(`Monto total: Bs. ${total.toLocaleString("es-VE", { maximumFractionDigits: 2 })}`, 40, 90);

      const head = [["Nº Comp.", "RIF", "Contribuyente", "Fecha", "Método", "Referencia", "Monto (Bs.)", "Estatus"]];
      const body = rows.map(p => [
        p.nro_comprobante || "—", p.contribuyente_rif || "—",
        (p.contribuyente_nombre || "—").slice(0, 40),
        p.fecha_pago || "", METODO_LABEL[p.metodo_pago] || p.metodo_pago || "",
        p.referencia || "—",
        Number(p.monto_pagado || 0).toLocaleString("es-VE", { maximumFractionDigits: 2 }),
        ESTADO_LABEL[p.estado_validacion] || p.estado_validacion || "",
      ]);
      autoTable(doc, {
        head, body, startY: 110,
        margin: { bottom: 60 },
        styles: { fontSize: 8, cellPadding: 4 },
        headStyles: { fillColor: [20, 83, 45], textColor: 255 },
        alternateRowStyles: { fillColor: [240, 253, 244] },
        columnStyles: { 6: { halign: "right" } },
        didDrawPage: (data) => drawPalmaFooter(doc, { logoDataURL, fecha, hora, pageNumber: data.pageNumber }),
      });
      doc.save(`${filename}.pdf`);
      toast.success(`PDF descargado (${rows.length} pagos)`);
    });
  }
}

function FiltrosPagos({ filters, onChange, includeEstado, totalCount, filteredCount, rowsToExport, contexto }) {
  const set = (k, v) => onChange({ ...filters, [k]: v });
  const active = filters.rif || filters.contribuyente || filters.desde || filters.hasta || filters.metodo !== "todos" || (includeEstado && filters.estado !== "todos");
  const clear = () => onChange({ rif: "", contribuyente: "", desde: "", hasta: "", estado: "todos", metodo: "todos" });
  return (
    <div className="mb-3 space-y-2">
      <div className={`grid grid-cols-2 md:grid-cols-3 ${includeEstado ? "lg:grid-cols-6" : "lg:grid-cols-5"} gap-2`}>
        <Input data-testid="rec-filtro-rif" value={filters.rif} onChange={e => set("rif", e.target.value)} placeholder="RIF (ej: 408512306)" className="bg-green-50/60 border-green-100 h-9" />
        <Input data-testid="rec-filtro-contribuyente" value={filters.contribuyente} onChange={e => set("contribuyente", e.target.value)} placeholder="Contribuyente" className="bg-green-50/60 border-green-100 h-9" />
        <Input data-testid="rec-filtro-desde" type="date" value={filters.desde} onChange={e => set("desde", e.target.value)} className="bg-green-50/60 border-green-100 h-9" title="Fecha desde" />
        <Input data-testid="rec-filtro-hasta" type="date" value={filters.hasta} onChange={e => set("hasta", e.target.value)} className="bg-green-50/60 border-green-100 h-9" title="Fecha hasta" />
        <Select value={filters.metodo} onValueChange={v => set("metodo", v)}>
          <SelectTrigger data-testid="rec-filtro-metodo" className="bg-green-50/60 border-green-100 h-9"><SelectValue placeholder="Método" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los métodos</SelectItem>
            <SelectItem value="transferencia">Transferencia</SelectItem>
            <SelectItem value="pago_movil">Pago Móvil</SelectItem>
            <SelectItem value="efectivo">Efectivo</SelectItem>
            <SelectItem value="punto_venta">Punto de Venta</SelectItem>
            <SelectItem value="deposito">Depósito</SelectItem>
          </SelectContent>
        </Select>
        {includeEstado && (
          <Select value={filters.estado} onValueChange={v => set("estado", v)}>
            <SelectTrigger data-testid="rec-filtro-estado" className="bg-green-50/60 border-green-100 h-9"><SelectValue placeholder="Estatus" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los estatus</SelectItem>
              <SelectItem value="validado">Validado</SelectItem>
              <SelectItem value="pendiente_validacion">Pendiente</SelectItem>
              <SelectItem value="rechazado">Rechazado</SelectItem>
              <SelectItem value="anulado">Anulado</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>
      <div className="flex items-center justify-between text-xs text-slate-500 px-1">
        <span data-testid="rec-filtro-summary">Mostrando <b className="text-green-800">{filteredCount}</b> de <b>{totalCount}</b> pago{totalCount === 1 ? "" : "s"}{active ? " · filtros activos" : ""}</span>
        <div className="flex items-center gap-1">
          {active && <Button size="sm" variant="ghost" onClick={clear} data-testid="rec-filtro-clear" className="h-7 text-red-600 hover:text-red-700">✕ Limpiar filtros</Button>}
          <Popover>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline" data-testid="rec-btn-exportar" disabled={!filteredCount} className="h-7 border-green-200 text-green-800 hover:bg-green-50">
                <Download className="w-3.5 h-3.5 mr-1" /> Exportar
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-52 p-1">
              <button data-testid="rec-export-excel" onClick={() => exportarPagos({ formato: "excel", rows: rowsToExport, contexto })} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-green-50 text-left">
                <FileSpreadsheet className="w-4 h-4 text-green-700" /> Excel (.xlsx)
              </button>
              <button data-testid="rec-export-pdf" onClick={() => exportarPagos({ formato: "pdf", rows: rowsToExport, contexto })} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-orange-50 text-left">
                <FileText className="w-4 h-4 text-orange-600" /> PDF (.pdf)
              </button>
              <button data-testid="rec-export-json" onClick={() => exportarPagos({ formato: "json", rows: rowsToExport, contexto })} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-slate-50 text-left">
                <FileJson className="w-4 h-4 text-slate-600" /> JSON (.json)
              </button>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
}


// ============================== Combobox buscable para Banco Emisor ==============================
function BancoEmisorCombobox({ bancos, value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = bancos.find(b => b.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button data-testid="rec-banco-emisor" type="button" variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal">
          <span className={selected ? "text-slate-900 truncate" : "text-slate-400"}>{selected ? `${selected.codigo} - ${selected.nombre}` : "Seleccione banco emisor"}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command
          filter={(itemValue, search) => {
            // itemValue trae "codigo nombre" en lowercase. search también en lowercase.
            const q = (search || "").trim().toLowerCase();
            if (!q) return 1;
            return itemValue.includes(q) ? 1 : 0;
          }}
        >
          <CommandInput data-testid="rec-banco-search" placeholder="Buscar por código o nombre…" className="h-9" />
          <CommandList>
            <CommandEmpty>Sin resultados</CommandEmpty>
            <CommandGroup>
              {bancos.map(b => (
                <CommandItem
                  key={b.id}
                  value={`${b.codigo || ""} ${b.nombre || ""}`.toLowerCase()}
                  onSelect={() => { onChange(b.id); setOpen(false); }}
                  data-testid={`rec-banco-item-${b.codigo}`}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === b.id ? "opacity-100 text-green-700" : "opacity-0"}`} />
                  <span className="font-mono text-xs text-slate-500 mr-2">{b.codigo}</span>
                  <span>{b.nombre}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}


// ============================== Formulario Registrar Recaudación ==============================
// Estructura para una línea de pago dentro del array `pagos` (permite pagos multi-parte).
const emptyPago = () => ({
  banco_id: "",
  cuenta_recaudadora_id: "",
  referencia: "",
  monto_pagado: "",
  fecha_pago: new Date().toISOString().slice(0, 10),
  comprobante_url: "",
});

function RegistrarRecaudacion({ auth, onCreated }) {
  const empty = {
    contribuyente_id: "",
    liquidacion_ids: [],
    estado_validacion: "pendiente_validacion",
    observaciones: "",
  };
  const [form, setForm] = useState(empty);
  const [pagos, setPagos] = useState([emptyPago()]);
  const { contribuyentes, contribSearch, setContribSearch } = useContribuyentesSearch({ initialLimit: 200 });
  const [bancos, setBancos] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [liqsPend, setLiqsPend] = useState([]);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/definiciones/bancos`, auth).then(r => setBancos(r.data));
    axios.get(`${API}/definiciones/cuentas-bancarias`, auth).then(r => setCuentas(r.data));
  // eslint-disable-next-line
  }, []);

  // Cuando cambia contribuyente, cargar liquidaciones pendientes
  useEffect(() => {
    if (form.contribuyente_id) {
      axios.get(`${API}/liquidaciones`, { ...auth, params: { contribuyente_id: form.contribuyente_id, estado: "pendiente" } })
        .then(r => setLiqsPend(r.data));
    } else { setLiqsPend([]); }
    setForm(f => ({ ...f, liquidacion_ids: [] }));
  // eslint-disable-next-line
  }, [form.contribuyente_id]);

  const cuentasFiltradas = cuentas;

  const totalSel = useMemo(
    () => liqsPend.filter(l => form.liquidacion_ids.includes(l.id)).reduce((a, b) => a + (b.monto || 0), 0),
    [form.liquidacion_ids, liqsPend],
  );
  const sumPagos = useMemo(
    () => pagos.reduce((a, p) => a + (Number(p.monto_pagado) || 0), 0),
    [pagos],
  );
  const restante = useMemo(() => Number((totalSel - sumPagos).toFixed(2)), [totalSel, sumPagos]);

  // Auto-completar el monto de la primera línea al seleccionar planillas (si sólo hay 1 línea)
  useEffect(() => {
    if (totalSel > 0 && pagos.length === 1 && (!pagos[0].monto_pagado || Number(pagos[0].monto_pagado) === 0)) {
      setPagos(prev => prev.map((p, i) => i === 0 ? { ...p, monto_pagado: totalSel } : p));
    }
  // eslint-disable-next-line
  }, [totalSel]);

  const togglePlanilla = (id) => setForm(f => ({ ...f, liquidacion_ids: f.liquidacion_ids.includes(id) ? f.liquidacion_ids.filter(x => x !== id) : [...f.liquidacion_ids, id] }));

  const updatePago = (idx, patch) => {
    setPagos(prev => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
  };
  const addPago = () => {
    // Al agregar una línea, autocompletar el monto con el faltante
    const faltante = Number((totalSel - sumPagos).toFixed(2));
    setPagos(prev => [...prev, { ...emptyPago(), monto_pagado: faltante > 0 ? faltante : "" }]);
  };
  const removePago = (idx) => {
    setPagos(prev => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));
  };

  const fullComprobanteUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return `${backendBase}${url}`;
  };

  const handleUploadLinea = async (idx, file) => {
    if (!file) return;
    if (!["image/jpeg","image/png","image/jpg","application/pdf"].includes(file.type)) { toast.error("Use JPG, PNG o PDF"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Archivo supera 8 MB"); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      setUploadingIdx(idx);
      const r = await axios.post(`${API}/recaudaciones/comprobante/upload`, fd, {
        headers: { Authorization: auth.headers.Authorization },
      });
      updatePago(idx, { comprobante_url: r.data.url });
      toast.success(`Comprobante #${idx + 1} subido`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir");
    } finally { setUploadingIdx(null); }
  };

  const submit = async () => {
    // === Validaciones globales ===
    if (!form.contribuyente_id) { toast.error("Seleccione el contribuyente"); return; }
    if (!form.liquidacion_ids.length) { toast.error("Seleccione al menos un trámite por pagar"); return; }
    if (!pagos.length) { toast.error("Debe registrar al menos un pago"); return; }

    // Validación por línea
    const seen = new Set();
    for (let i = 0; i < pagos.length; i++) {
      const p = pagos[i];
      const n = i + 1;
      if (!p.banco_id) { toast.error(`Pago #${n}: seleccione el Banco Emisor`); return; }
      if (!p.cuenta_recaudadora_id) { toast.error(`Pago #${n}: seleccione la Cuenta Recaudadora`); return; }
      if (!p.fecha_pago) { toast.error(`Pago #${n}: ingrese la fecha`); return; }
      const ref = String(p.referencia || "").trim();
      if (ref.length < 6) { toast.error(`Pago #${n}: la referencia debe tener al menos 6 caracteres`); return; }
      if (!p.monto_pagado || Number(p.monto_pagado) <= 0) { toast.error(`Pago #${n}: ingrese el monto`); return; }
      if (!p.comprobante_url) { toast.error(`Pago #${n}: adjunte el comprobante de esta transferencia`); return; }
      const key = `${p.banco_id}||${ref}`;
      if (seen.has(key)) { toast.error(`Pago #${n}: referencia repetida en otra línea del mismo banco`); return; }
      seen.add(key);
    }

    // Validar que la suma cubra al menos el total. El excedente queda como crédito fiscal.
    if (sumPagos + 0.01 < totalSel) {
      const falta = (totalSel - sumPagos).toFixed(2);
      toast.error(`La suma de los pagos (Bs. ${sumPagos.toFixed(2)}) es menor al total a pagar (Bs. ${totalSel.toFixed(2)}). Faltan Bs. ${falta}. Aumente los montos.`);
      return;
    }
    const excedenteBs = sumPagos > totalSel ? (sumPagos - totalSel) : 0;
    if (excedenteBs > 0.01 && !window.confirm(
      `El pago excede en Bs. ${excedenteBs.toFixed(2)} el monto de las planillas seleccionadas.\n\n` +
      `Este excedente quedará como CRÉDITO FISCAL a favor del contribuyente para próximos pagos.\n\n` +
      `¿Deseas continuar?`,
    )) {
      return;
    }

    try {
      setSaving(true);
      const detalles = pagos.map(p => ({
        banco_id: p.banco_id,
        cuenta_recaudadora_id: p.cuenta_recaudadora_id,
        referencia: String(p.referencia).trim(),
        monto_pagado: Number(p.monto_pagado),
        fecha_pago: p.fecha_pago,
        comprobante_url: p.comprobante_url || null,
      }));
      // Payload: si hay 1 sola línea, también enviamos los campos planos para retro-compat.
      // Si hay >1, el backend usa el array `detalles_pago` como fuente de verdad.
      const primer = detalles[0];
      const payload = {
        contribuyente_id: form.contribuyente_id,
        liquidacion_ids: form.liquidacion_ids,
        estado_validacion: form.estado_validacion,
        observaciones: form.observaciones,
        // Campos planos (derivados del primer detalle) — retrocompatibilidad
        banco_id: primer.banco_id,
        cuenta_recaudadora_id: primer.cuenta_recaudadora_id,
        referencia: primer.referencia,
        fecha_pago: primer.fecha_pago,
        comprobante_url: primer.comprobante_url,
        monto_pagado: Number(sumPagos.toFixed(2)),
        metodo_pago: "transferencia",
        detalles_pago: detalles.length > 1 ? detalles : null,
      };
      const r = await axios.post(`${API}/recaudaciones/pago`, payload, auth);
      const cred = Number(r.data?.credito_generado || 0);
      const baseMsg = r.data.nro_comprobante ? `Recaudación registrada · ${r.data.nro_comprobante}` : "Recaudación registrada · Nº se asignará al validar";
      if (cred > 0) {
        toast.success(`${baseMsg} · Crédito fiscal generado: Bs. ${cred.toFixed(2)}`, { duration: 6000 });
      } else {
        toast.success(baseMsg);
      }
      setForm(empty);
      setPagos([emptyPago()]);
      onCreated && onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const fmt = (n) => `Bs. ${(Number(n)||0).toLocaleString("es-VE",{maximumFractionDigits:2})}`;

  const contribFiltrados = useMemo(() => {
    const q = (contribSearch || "").trim().toLowerCase();
    if (!q) return contribuyentes;
    return contribuyentes.filter(c => {
      const txt = [c.tipo_documento, c.documento, `${c.tipo_documento||""}-${c.documento||""}`, c.razon_social, c.nombre, c.apellido, c.numero_lae, c.codigo_catastral].filter(Boolean).join(" ").toLowerCase();
      return txt.includes(q);
    });
  }, [contribSearch, contribuyentes]);

  const isMulti = pagos.length > 1;

  return (
    <Card className="p-5 border-green-100 space-y-5" data-testid="rec-form-registrar">
      {/* Fila 1: Contribuyente */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>Contribuyente*</Label>
          <ContribuyenteCombobox
            value={form.contribuyente_id}
            onChange={(id) => setForm({...form, contribuyente_id: id})}
            contribuyentes={contribuyentes}
            search={contribSearch}
            onSearchChange={setContribSearch}
            placeholder="Seleccione contribuyente"
            testId="rec-contrib"
          />
        </div>
        <div>
          <Label>Estatus*</Label>
          <Select value={form.estado_validacion} onValueChange={v => setForm({...form, estado_validacion: v})}>
            <SelectTrigger data-testid="rec-estatus"><SelectValue /></SelectTrigger>
            <SelectContent>{ESTATUS_OPTS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Fila 2: Trámites por pagar (checklist) */}
      <div>
        <Label>Trámite Por Pagar* <span className="text-xs text-slate-500">· {form.liquidacion_ids.length} seleccionado(s) · Total: {fmt(totalSel)}</span></Label>
        <div className="border border-green-100 rounded-lg max-h-56 overflow-y-auto bg-white">
          {!form.contribuyente_id && <p className="text-center text-slate-400 py-6 text-sm">Seleccione un contribuyente para ver sus trámites pendientes</p>}
          {form.contribuyente_id && liqsPend.length === 0 && <p className="text-center text-slate-400 py-6 text-sm">Sin liquidaciones pendientes para este contribuyente</p>}
          {liqsPend.map(l => (
            <label key={l.id} className={`flex items-center gap-3 p-3 border-b border-green-50 cursor-pointer transition ${form.liquidacion_ids.includes(l.id) ? "bg-green-50" : "hover:bg-green-50/40"}`} data-testid={`rec-liq-${l.periodo}`}>
              <input type="checkbox" checked={form.liquidacion_ids.includes(l.id)} onChange={() => togglePlanilla(l.id)} className="w-4 h-4 accent-green-700" />
              <div className="flex-1">
                <p className="font-mono text-[11px] text-slate-500">{l.periodo} · {l.nro_planilla}</p>
                <p className="text-sm text-green-900 font-medium">{l.descripcion}</p>
              </div>
              <p className="font-mono font-semibold text-green-900">{fmt(l.monto)}</p>
            </label>
          ))}
        </div>
      </div>

      {/* ===== Líneas de pago (multi-parte) ===== */}
      <div className="space-y-3" data-testid="rec-lineas-pago">
        <div className="flex items-center justify-between">
          <Label className="text-green-900">
            Pagos {isMulti && <span className="text-xs font-normal text-orange-700">· Multi-parte ({pagos.length} transferencias)</span>}
          </Label>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={addPago}
            className="border-orange-300 text-orange-700 hover:bg-orange-50"
            data-testid="rec-btn-agregar-pago"
          >
            + Agregar pago
          </Button>
        </div>

        {pagos.map((p, idx) => (
          <div
            key={idx}
            className={`relative rounded-lg border p-4 ${isMulti ? "border-orange-200 bg-orange-50/30" : "border-green-100 bg-white"}`}
            data-testid={`rec-pago-linea-${idx}`}
          >
            {isMulti && (
              <div className="absolute -top-2.5 left-4 bg-white px-2 text-[11px] font-semibold text-orange-700 border border-orange-200 rounded">
                Pago #{idx + 1}
              </div>
            )}
            {isMulti && (
              <button
                type="button"
                onClick={() => removePago(idx)}
                className="absolute top-2 right-2 text-red-500 hover:text-red-700 text-xs font-medium"
                data-testid={`rec-btn-quitar-pago-${idx}`}
              >
                ✕ Quitar
              </button>
            )}

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Banco Emisor*</Label>
                <BancoEmisorCombobox
                  bancos={bancos}
                  value={p.banco_id}
                  onChange={v => updatePago(idx, { banco_id: v })}
                />
              </div>
              <div>
                <Label className="text-xs">Cuenta Recaudadora*</Label>
                <Select value={p.cuenta_recaudadora_id} onValueChange={v => updatePago(idx, { cuenta_recaudadora_id: v })}>
                  <SelectTrigger data-testid={`rec-cuenta-${idx}`}><SelectValue placeholder={cuentasFiltradas.length ? "Seleccione cuenta" : "Sin cuentas"} /></SelectTrigger>
                  <SelectContent>
                    {cuentasFiltradas.length === 0
                      ? <SelectItem disabled value="_empty">Sin cuentas registradas</SelectItem>
                      : cuentasFiltradas.map(c => <SelectItem key={c.id} value={c.id}>{c.banco_nombre} · {c.numero_cuenta} · {tipoCuentaLabel(c.tipo)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Nro. de Referencia* <span className="text-[10px] text-slate-400">· mín. 6</span></Label>
                <Input
                  data-testid={`rec-referencia-${idx}`}
                  value={p.referencia}
                  onChange={e => updatePago(idx, { referencia: e.target.value })}
                  placeholder="Ej. 0123456789"
                  minLength={6}
                  aria-invalid={!!p.referencia && String(p.referencia).trim().length < 6}
                  className={p.referencia && String(p.referencia).trim().length < 6 ? "border-red-400 focus-visible:ring-red-400" : ""}
                />
              </div>
              <div>
                <Label className="text-xs">Fecha de Recaudación*</Label>
                <Input
                  data-testid={`rec-fecha-${idx}`}
                  type="date"
                  value={p.fecha_pago}
                  onChange={e => updatePago(idx, { fecha_pago: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Monto Pagado*</Label>
                <Input
                  data-testid={`rec-monto-${idx}`}
                  type="number"
                  step="0.01"
                  value={p.monto_pagado}
                  onChange={e => updatePago(idx, { monto_pagado: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label className="text-xs">Comprobante* <span className="text-[10px] text-slate-400">· JPG/PNG/PDF</span></Label>
                <div className="flex items-center gap-2">
                  <input
                    id={`rec-comp-${idx}`}
                    type="file"
                    accept="image/jpeg,image/png,application/pdf"
                    className="hidden"
                    onChange={e => handleUploadLinea(idx, e.target.files?.[0])}
                    data-testid={`rec-comprobante-input-${idx}`}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={uploadingIdx === idx}
                    onClick={() => document.getElementById(`rec-comp-${idx}`).click()}
                    data-testid={`rec-comprobante-btn-${idx}`}
                  >
                    <Upload className="w-3.5 h-3.5 mr-1.5" />
                    {uploadingIdx === idx ? "Subiendo..." : p.comprobante_url ? "Cambiar" : "Subir"}
                  </Button>
                  {p.comprobante_url && (
                    <a
                      href={fullComprobanteUrl(p.comprobante_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange-600 text-xs inline-flex items-center gap-1 underline hover:text-orange-700"
                      data-testid={`rec-comprobante-link-${idx}`}
                    >
                      <FileText className="w-3.5 h-3.5" />Ver
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Resumen de totales */}
        <div
          className={`rounded-lg p-3 border-2 ${
            Math.abs(sumPagos - totalSel) < 0.01 && totalSel > 0
              ? "border-green-400 bg-green-50"
              : sumPagos > totalSel && totalSel > 0
              ? "border-emerald-400 bg-emerald-50"
              : totalSel > 0
              ? "border-orange-300 bg-orange-50"
              : "border-slate-200 bg-slate-50"
          }`}
          data-testid="rec-resumen-totales"
        >
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div>
              <p className="text-[11px] text-slate-500 uppercase font-semibold">Total a pagar</p>
              <p className="font-mono font-bold text-green-900">{fmt(totalSel)}</p>
            </div>
            <div>
              <p className="text-[11px] text-slate-500 uppercase font-semibold">Suma de pagos</p>
              <p className="font-mono font-bold text-blue-900" data-testid="rec-suma-pagos">{fmt(sumPagos)}</p>
            </div>
            <div>
              <p className="text-[11px] text-slate-500 uppercase font-semibold">
                {sumPagos > totalSel ? "Excedente (crédito fiscal)" : Math.abs(restante) < 0.01 ? "Diferencia" : "Faltante"}
              </p>
              <p
                className={`font-mono font-bold ${
                  Math.abs(restante) < 0.01
                    ? "text-green-700"
                    : restante > 0
                    ? "text-red-700"
                    : "text-emerald-700"
                }`}
                data-testid="rec-diferencia"
              >
                {fmt(Math.abs(restante))}
                {Math.abs(restante) < 0.01 ? " ✓" : restante > 0 ? " (falta)" : " ★ crédito"}
              </p>
            </div>
          </div>
          {sumPagos > totalSel && totalSel > 0 && (
            <p className="mt-2 text-[11px] text-emerald-800 bg-emerald-100 rounded px-2 py-1.5" data-testid="rec-aviso-credito">
              💰 El excedente de <b>{fmt(sumPagos - totalSel)}</b> quedará como <b>crédito fiscal</b> a favor del contribuyente, aplicable a pagos futuros.
            </p>
          )}
        </div>
      </div>

      {/* Observaciones */}
      <div>
        <Label>Observaciones</Label>
        <Textarea data-testid="rec-observaciones" rows={2} value={form.observaciones} onChange={e => setForm({...form, observaciones: e.target.value})} />
      </div>

      <div className="flex justify-end pt-2 border-t border-green-100">
        <Button
          data-testid="rec-btn-submit"
          disabled={saving || uploadingIdx !== null}
          onClick={submit}
          className="bg-orange-500 hover:bg-orange-600 text-white"
        >
          {saving ? "Registrando..." : `Registrar recaudación${isMulti ? ` (${pagos.length} pagos)` : ""}`}
        </Button>
      </div>
    </Card>
  );
}
